import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useGeneration } from '../context/GenerationContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { ChatInput } from '../components/chat/ChatInput';
import { ChatHistory } from '../components/chat/ChatHistory';
import { ChatParticipants } from '../components/chat/ChatParticipants';
import { ChatMessage, ChatParticipant } from '../types/chat';
import { PageHeader } from '../components/PageHeader';
import { NavigationMenu } from '../components/NavigationMenu';
import { Button } from '../components/Button';
import { MessageCircle } from 'lucide-react';

export default function Chat() {
  useDocumentTitle('Чат');
  // Удалена неиспользуемая переменная tasks
  const navigate = useNavigate();
  const { chatId } = useParams<{ chatId: string }>();
  const location = useLocation();
  
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [participants] = useState<ChatParticipant[]>([
    {
      id: 'user123',
      name: 'Вы',
      role: 'Пользователь',
      isOnline: true
    },
    {
      id: 'assistant',
      name: 'Ассистент',
      role: 'AI',
      isOnline: true
    }
  ]);

  const currentUserId = "user123";
  
  // Эффект для загрузки сообщений при изменении chatId
  useEffect(() => {
    if (chatId) {
      // Здесь будет логика загрузки сообщений конкретного чата по ID
      // Например:
      // loadChatMessages(chatId);
      console.log(`Загрузка сообщений для чата с ID: ${chatId}`);
    }
  }, [chatId]);

  const handleDeleteMessage = (messageId: string) => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
  };
  
  const handleSendMessage = (content: string) => {
    const newMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      content,
      sender: participants.find(p => p.id === currentUserId)!,
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, newMessage]);
    
    // Здесь можно добавить логику для отправки сообщения на сервер
    // и получения ответа от ассистента
  };
  
  const handleTogglePause = () => {
    setIsPaused(prev => !prev);
  };
  
  // Функция для создания нового чата
  const createNewChat = () => {
    // Генерируем уникальный ID для нового чата
    const newChatId = `chat-${Date.now()}`;
    // Обновляем URL с новым идентификатором чата
    navigate(`/chat/${newChatId}`);
    // Сбрасываем сообщения
    setMessages([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900">
      {/* Навигационное меню для десктопа */}
      <div className="hidden lg:block">
        <NavigationMenu />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <PageHeader
          title="Чат"
          subtitle="Интеллектуальный помощник для решения ваших задач"
        />

        {/* Основное содержимое чата */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 overflow-hidden">
          <div className="flex h-[calc(100vh-300px)]">
            <div className="w-64 bg-gray-50/80 dark:bg-gray-700/50 p-4 border-r border-gray-200 dark:border-gray-600">
              <ChatParticipants participants={participants} />
              <Button 
                variant="primary"
                fullWidth
                className="mt-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg transition-all duration-200 transform hover:scale-105 border-none"
                onClick={createNewChat}
              >
                Новый чат
              </Button>
            </div>
            <div className="flex-1 flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <ChatHistory 
                  messages={messages} 
                  currentUserId={currentUserId}
                  onDeleteMessage={handleDeleteMessage}
                />
              </div>
              <div className="p-4 border-t border-gray-200 dark:border-gray-600">
                {showLimitMessage && (
                  <div className="text-red-500 text-sm mb-2 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">
                    Достигнут лимит задач в процессе. Дождитесь завершения текущих задач.
                  </div>
                )}
                <ChatInput 
                  onSendMessage={handleSendMessage}
                  disabled={showLimitMessage} 
                  isPaused={isPaused}
                  onTogglePause={handleTogglePause}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
